/**
 * THE REACTION PALETTE, AS SIX NAMED CATEGORIES.
 *
 * ── WHY THERE IS NO FAMILY LEVEL HERE ─────────────────────────────────────────────────────────
 * The equation editor splits into Symbols and Frames because 186 symbols divide exactly: 141
 * place a glyph, 45 open a frame with holes. Chemistry has far fewer, and only two of them open
 * anything — the labelled arrow and the nuclide. A two-level structure over that would be
 * ceremony, so the categories sit on one row.
 *
 * ── WHAT THE OLD THREE TABS WERE HIDING ───────────────────────────────────────────────────────
 * Arrows, "Chemistry" and Nuclear. The middle one was a leftovers drawer: it held Δ and hν,
 * which are genuinely reaction conditions, alongside ° (a unit mark) and · (a hydrate joiner)
 * that were there because nothing else claimed them. Splitting it named what each thing is.
 *
 * And three whole kinds of symbol had no buttons at all. State symbols were typed by hand
 * against an invisible contract — the renderer italicises `(s|l|g|aq)` by exact match, so `(Aq)`
 * or `(aqueous)` silently stayed upright with nothing to say why. Charges needed `^2+`, where
 * the caret is not guessable and sits on a second keyboard layer on a phone. Bonds did not exist,
 * so `H-C#C-H` could not be written at all.
 *
 * ── ORDER IS BY REACH, AND FIXED ──────────────────────────────────────────────────────────────
 * As in the equation editor: the row never reorders, so what you reach for without looking stays
 * put, and the adapting happens in Common.
 */

import { elementName } from './element-names';
import { ARROWS, type DrawnArrow } from './reaction-glyphs';

/** `code` is mhchem source, inserted at the cursor. `cursor` places the caret inside it. */
export type Sym = {
  sym: string;
  code: string;
  /** A locale key, or `{ element }` for a symbol named from the periodic table. */
  aria: string | { element: string };
  /**
   * A locale key holding EXTRA WORDS TO BE FOUND BY, space separated, never shown.
   *
   * One name per symbol is enough to label a row and not enough to find one. A charge is a
   * charge, a plus, a cation and an ion depending on who is reaching for it, and the Portuguese
   * words are different again — `cátion`, `mais`. Putting them all in the name would make the
   * result row unreadable to buy a search tier, so they ride beside it instead.
   */
  alias?: string;
  cursor?: number;
  /** A button whose glyph has to be DRAWN, because no font the field uses carries it. */
  special?: 'nuclide' | DrawnArrow;
  /**
   * This row's `sym` BELONGS TO ANOTHER ROW, so it may not claim that character in the display
   * table. Both unequal equilibria are written ⇌ because Unicode has nothing closer, and if
   * either claimed it then `⇌` would read back as the lopsided code rather than the balanced
   * one. Being drawn is NOT the same fact: ⇄ and ⇌ are drawn too, and they own their
   * characters — the table keeps them, which is what makes `N2 <=> 2NH3` legible outside the
   * editor, where there are no node views to draw anything.
   */
  borrowed?: true;
  /**
   * How the BUTTON draws this, where one line of Unicode cannot say it.
   *
   * THE BUTTON ONLY. `sym` stays what the FIELD shows for this code — the display table is
   * built from it — so a face that reads better on a key never changes what a teacher sees in
   * the formula. That split is why this is not simply a better `sym`.
   *
   *   stack      a nuclide's mass and number, set over each other before the symbol, which
   *              `¹₀n` only approximates by putting them side by side
   *   overArrow  what is written ON an arrow, which is above it and not after it
   *   big        drawn at the size the glyph needs: ⇌ is two harpoons in the space → uses one
   */
  btn?:
    | { kind: 'stack'; sup: string; sub: string; base: string }
    | { kind: 'overArrow'; above?: string; below?: string }
    | { kind: 'big' }
    /* The co-substrate arc, which is drawn from its own module: it needs room above and below
       the shaft, so it is not one of the fixed-box arrows and cannot borrow their face. */
    | { kind: 'arc' };
};

/** A run of symbols under a heading, as the equation editor's categories are built. */
export type Group = { labelKey: string; items: Sym[] };

export type Category = {
  id: string;
  labelKey: string;
  /**
   * The headed runs, where a category has them. THE SOURCE OF TRUTH when present.
   *
   * `items` below stays the flat list every other reader wants -- the name index, the display
   * table's round-trip corpus, the render invariant -- and is DERIVED from this rather than
   * written beside it, so the two cannot disagree. A find-only category has no groups and
   * writes its items directly.
   */
  groups?: Group[];
  items: Sym[];
  /** Elements only — it draws a keyboard of the periodic table rather than a flat grid. */
  elements?: boolean;
  /** Structures only — a searchable library of DRAWN molecules, not a grid of glyphs. */
  structures?: boolean;
  /**
   * Reachable by SEARCH, not by browsing. It keeps its items and its name; it loses its tab.
   *
   * Not `hidden`, because nothing about it is hidden: a result row still says which category
   * the thing belongs to, and that is what the word has to go on meaning once the tab is gone.
   * The flag says where you can get at it from, not whether it exists.
   */
  findOnly?: boolean;
};

/**
 * The elements school chemistry actually writes, in periodic order.
 *
 * Not the whole table: 118 buttons is a worse first screen than the twenty-odd that cover almost
 * every reaction a teacher sets. The rest are a press away — see the Elements panel.
 */
export const SCHOOL_ELEMENTS = [
  'H', 'He', 'Li', 'Be', 'B', 'C', 'N', 'O', 'F', 'Ne',
  'Na', 'Mg', 'Al', 'Si', 'P', 'S', 'Cl', 'Ar',
  'K', 'Ca', 'Mn', 'Fe', 'Cu', 'Zn', 'Br', 'Ag', 'I', 'Ba', 'Pb',
] as const;

/** Every element symbol, for the full table behind the Elements panel. */
export const ALL_ELEMENTS = [
  'H', 'He', 'Li', 'Be', 'B', 'C', 'N', 'O', 'F', 'Ne',
  'Na', 'Mg', 'Al', 'Si', 'P', 'S', 'Cl', 'Ar',
  'K', 'Ca', 'Sc', 'Ti', 'V', 'Cr', 'Mn', 'Fe', 'Co', 'Ni', 'Cu', 'Zn',
  'Ga', 'Ge', 'As', 'Se', 'Br', 'Kr',
  'Rb', 'Sr', 'Y', 'Zr', 'Nb', 'Mo', 'Tc', 'Ru', 'Rh', 'Pd', 'Ag', 'Cd',
  'In', 'Sn', 'Sb', 'Te', 'I', 'Xe',
  'Cs', 'Ba', 'La', 'Ce', 'Pr', 'Nd', 'Pm', 'Sm', 'Eu', 'Gd', 'Tb', 'Dy',
  'Ho', 'Er', 'Tm', 'Yb', 'Lu', 'Hf', 'Ta', 'W', 'Re', 'Os', 'Ir', 'Pt',
  'Au', 'Hg', 'Tl', 'Pb', 'Bi', 'Po', 'At', 'Rn',
  'Fr', 'Ra', 'Ac', 'Th', 'Pa', 'U', 'Np', 'Pu', 'Am', 'Cm', 'Bk', 'Cf',
  'Es', 'Fm', 'Md', 'No', 'Lr', 'Rf', 'Db', 'Sg', 'Bh', 'Hs', 'Mt', 'Ds',
  'Rg', 'Cn', 'Nh', 'Fl', 'Mc', 'Lv', 'Ts', 'Og',
] as const;

const asElement = (sym: string): Sym => ({ sym, code: sym, aria: { element: sym } });

/** The name a button announces, which for an element comes from the shared 118-entry table. */
export function elementLabel(sym: string): string {
  const name = elementName(sym);
  return name ? `${sym}, ${name}` : sym;
}

/** Written with `groups`; `items` is filled in below so nothing else has to know. */
type RawCategory = Omit<Category, 'items'> & { items?: Sym[] };

const RAW_CATEGORIES: RawCategory[] = [
  {
    /*
     * EVERYTHING A REACTION IS WRITTEN WITH, in groups rather than tabs.
     *
     * Arrows, Species and Particles were three tabs of thirteen to eighteen glyphs, and the
     * owner's read was that a tab per small grouping is ceremony -- the equation editor already
     * shows the alternative, which is groups inside a larger category.
     *
     * ── WHY NOT CHEMISTRY AND PHYSICS ─────────────────────────────────────────────────────────
     * That was the owner's first instinct and the inventory argues against it. Nuclear decay is
     * school CHEMISTRY -- a radioactivity lesson writes ^238U -> ^234Th + alpha -- so putting
     * alpha, beta, gamma, n and p under Physics misfiles them for the teacher most likely to
     * reach for them, and putting them under Chemistry takes them from the physics teacher.
     * Only seven of the forty-two are physics-only: the pions, the muons and the neutrinos.
     *
     * Splitting on WHAT A THING IS rather than who teaches it needs no such adjudication. A
     * reaction is written with these; a particle is a particle whichever room it is named in.
     *
     * It also has room for what is queued. Feynman diagrams become a group under Particles,
     * enthalpy and mechanisms groups here -- where under a tab-per-grouping scheme each would
     * have wanted a tab of its own, which is the fragmentation this is answering.
     *
     * A subject split was considered and declined once before, on the grounds that arrows and
     * charges belong to every subject so a tab would duplicate or misfile them. That objection
     * was written when browse was the only way in. The suggester answers by name now, so a
     * browse home has to be reasonable rather than the only route.
     */
    id: 'reaction', labelKey: 'reactions.categories.reaction',
    groups: [
      { labelKey: 'reactions.groups.arrows', items: [
        { sym: '→',   code: '->',  aria: 'reactions.symbols.rightArrow', special: 'right' },
        { sym: '←',   code: '<-',  aria: 'reactions.symbols.leftArrow', special: 'left' },
        { sym: '↔',   code: '<->', aria: 'reactions.symbols.bothArrows', special: 'bothHeads' },
        /* NOT the resonance arrow above it: this one says the reaction runs both ways. */
        { sym: '⇄',   code: '<-->', aria: 'reactions.symbols.bothWaysArrow', special: 'bothWays' },
        { sym: '⇌',   code: '<=>',  aria: 'reactions.symbols.equilibrium', special: 'equilibrium' },
        /* An equilibrium that mostly sits one way. mhchem draws the harpoons unequal.
        All four are DRAWN: no font the field falls back to contains ⇄ or ⇌ at all, so the
        inserted glyph was the reader's machine's guess rather than the one on the button. */
        { sym: '⇌',   code: '<=>>',        aria: 'reactions.symbols.equilibriumForward', special: 'eqForward', borrowed: true },
        { sym: '⇌',   code: '<<=>',        aria: 'reactions.symbols.equilibriumBackward', special: 'eqBackward', borrowed: true },
        /*
         * THE TWO THAT ARE LaTeX RATHER THAN mhchem, and they are arrows all the same.
         *
         * Retrosynthesis reads right to left — "is made from" — and an organic syllabus writes
         * it constantly. The dashed one is a step proposed rather than performed.
         *
         * The scope accepted that these two could never take a decoration, on the grounds that
         * a LaTeX escape is not an mhchem arrow. Measured instead of assumed: `\Rightarrow[cat]`
         * and `\dashrightarrow[a][b]` both render. So there are not two classes of arrow after
         * all, and they are DRAWN like the rest — which is what puts them in `ARROW_CODES` and
         * hands them the decoration control for free.
         */
        { sym: '⇒',   code: '\\Rightarrow',     aria: 'reactions.symbols.retrosynthetic', special: 'doubleRight' },
        { sym: '⇢',   code: '\\dashrightarrow', aria: 'reactions.symbols.dashedArrow',    special: 'dashedRight' },
        /*
         * THE CO-SUBSTRATE ARC, which inserts Agavi's own syntax rather than mhchem.
         *
         * `frameFor` reads the code to find the frame it opens, so the key carries an EMPTY
         * one — the two holes are what the teacher fills, exactly as the labelled arrows did
         * before they became a decoration.
         *
         * No `cursor`: that places a caret inside inserted TEXT, and this opens a FRAME, whose
         * first empty hole the field finds on its own. An invariant holds the two apart.
         */
        { sym: '\u2933', code: '[cosub:->||]', btn: { kind: 'arc' }, aria: 'reactions.symbols.coSubstrate' },
        /*
         * THE FOUR LABELLED ARROWS ARE GONE, and what replaced them is a control.
         *
         * They were `->[\Delta]`, `->[$h\nu$]`, `->[]` and `->[][]` — four keys, every one of
         * them the YIELDING arrow. So a label could only ever go on `->`, and a CATALYSED
         * EQUILIBRIUM could not be authored at all: Haber and the Contact process, both of them
         * school chemistry, unwritable in an editor built for school chemistry.
         *
         * Covering that properly with keys means seven arrows times three decorations, which is
         * twenty-one keys and not a palette. The decoration is a control in the styling row
         * instead — see `reaction-arrow-frame` — and it works on whichever arrow is selected.
         *
         * No Δ and hν presets on it either. Both are keys two inches away and an empty box
         * accepts them, so a preset would save one tap and cost two more buttons.
         */
      ] },
      { labelKey: 'reactions.groups.conditions', items: [
        /*
        * WHAT THE REACTION RUNS UNDER, which is written ON the arrow and never beside it.
        *
        * Conditions was its own tab for as long as there were nine of them. A teacher writing
        * `->[\Delta]` reaches for the arrow and the delta in one movement, and a tab boundary
        * between the two made that two journeys. "Arrows" names the whole construct, label and
        * all, which is why the merged category keeps the arrow's name rather than gaining a
        * compound one.
        */
        
        /* What a reaction runs under, and nothing else. `·` left for Bonds, where a joiner belongs. */
        { sym: 'Δ',  code: '\\Delta', aria: 'reactions.symbols.delta'  },
        /*
         * `$...$` so BOTH letters are italic.
         *
         * h is the Planck constant and ν is a frequency -- quantity symbols, which ISO 80000-2
         * and IUPAC both set italic. Measured as shipped, `h\nu` inside mhchem drew the h
         * UPRIGHT and the ν italic: mhchem sets letters upright and KaTeX draws a Greek command
         * italic whatever is around it, so the pair disagreed with itself. Escaping to maths
         * mode gives both.
         */
        { sym: 'hν', code: '$h\\nu$',   aria: 'reactions.symbols.photon' },
        { sym: '°',  code: '^\\circ', btn: { kind: 'big' }, aria: 'reactions.symbols.degree' },
      ] },
      { labelKey: 'reactions.groups.cells', items: [
        /* A cell diagram's connectives, which is what an arrow is. The escaped forms are
        deliberate: a bare `||` draws as two separate bars where `$\|$` draws one ∥. */
        { sym: '∣',   code: '$|$',         aria: 'reactions.symbols.phaseBoundary' },
        /* Two separate bars on the BUTTON, where the single ∥ glyph sat only 1.5px wider than
        the phase boundary beside it and the two were not tellable apart. The inserted
        source is unchanged, so the reaction still renders the proper ∥. */
        { sym: '∣∣',  code: '$\\|$',       aria: 'reactions.symbols.saltBridge'    },
      ] },
      { labelKey: 'reactions.groups.bonds', items: [
        /* mhchem's own bond characters. `#` is the triple bond and survives the macro expansion
        that a `#`-leading colour does not, because it is not followed by a digit. */
        { sym: '–', code: '-', aria: 'reactions.symbols.singleBond' },
        { sym: '=', code: '=', aria: 'reactions.symbols.doubleBond' },
        { sym: '≡', code: '#', aria: 'reactions.symbols.tripleBond' },
        /* One glyph, two jobs: the dot between a salt and its water of crystallisation, and the
        one marking a radical. Both are "joined to", which is what a bond is. */
        { sym: '·', code: '\\cdot', aria: 'reactions.symbols.dot' },
      ] },
      { labelKey: 'reactions.groups.states', items: [
        /* Typed by hand until now, against an exact match the teacher could not see: the renderer
        italicises `(s|l|g|aq)` and anything else stays upright, unexplained. */
        { sym: '(s)',  code: '(s)',  aria: 'reactions.symbols.solid'   },
        { sym: '(l)',  code: '(l)',  aria: 'reactions.symbols.liquid'  },
        { sym: '(g)',  code: '(g)',  aria: 'reactions.symbols.gas'     },
        { sym: '(aq)', code: '(aq)', aria: 'reactions.symbols.aqueous' },
        { sym: '↑',   code: '^',           aria: 'reactions.symbols.upArrow'     },
        { sym: '↓',   code: 'v',           aria: 'reactions.symbols.downArrow'   },
      ] },
    ],
  },
  {
    /*
     * PARTICLES, which is what they are in either subject.
     *
     * Nucleons and decay products first, because a chemistry syllabus reaches them and a
     * physics one passes through; the leptons and mesons after, which are where it stops being
     * shared. The nuclide template leads its group: it is the general form the rest are
     * instances of, and a teacher writing any nuclide at all starts there.
     */
    id: 'particles', labelKey: 'reactions.categories.particles',
    groups: [
      { labelKey: 'reactions.groups.nuclei', items: [
        { sym: '⁴₂He', code: '^{}_{}', cursor: 2, btn: { kind: 'stack', sup: 'A', sub: 'Z', base: 'X' }, aria: 'reactions.symbols.nuclide', special: 'nuclide' },
        /* The neutron is what makes fission writable at all, and had no button. */
        { sym: '¹₀n',  code: '^{1}_{0}n', btn: { kind: 'stack', sup: '1', sub: '0', base: 'n' },       aria: 'reactions.symbols.neutron'      },
        { sym: '¹₁p',  code: '^{1}_{1}p', btn: { kind: 'stack', sup: '1', sub: '1', base: 'p' },       aria: 'reactions.symbols.proton'       },
      ] },
      /*
       * THE TWO HYDROGEN ISOTOPES FUSION IS WRITTEN WITH, their own run rather than Nuclei's.
       *
       * A shortcut and not a capability — the template above builds either in three taps —
       * but they are written often enough that three taps is three too many.
       *
       * Their own run partly because fusion reaches for the pair together, and partly for the
       * packer: the quarks below took Particles to an odd number of short runs, which leaves
       * a column ragged, and this is the honest place to find the extra one.
       */
      { labelKey: 'reactions.groups.isotopes', items: [
        { sym: '²₁H',  code: '^{2}_{1}H', btn: { kind: 'stack', sup: '2', sub: '1', base: 'H' },       aria: 'reactions.symbols.deuteron'     },
        { sym: '³₁H',  code: '^{3}_{1}H', btn: { kind: 'stack', sup: '3', sub: '1', base: 'H' },       aria: 'reactions.symbols.triton'       },
      ] },
      { labelKey: 'reactions.groups.decay', items: [
        { sym: 'α',  code: '\\alpha{}',     aria: 'reactions.symbols.alpha'        },
        { sym: 'β⁻', code: '\\beta^-{}',    aria: 'reactions.symbols.betaMinus'    },
        { sym: 'β⁺', code: '\\beta^+{}',    aria: 'reactions.symbols.betaPlus'     },
        { sym: 'γ',  code: '\\gamma{}',     aria: 'reactions.symbols.gamma'        },
      ] },
      /*
       * THE CHARGED LEPTONS, AND THE NEUTRINOS BESIDE THEM RATHER THAN AMONG THEM.
       *
       * One group until the tau arrived and took it to eight, which is more than a column holds
       * — and the layout allows a category only one full-width run, which Particles would then
       * have had in the middle of itself rather than at the front. The alternative was to move
       * leptons to the front, and the order here is pedagogical: a chemistry syllabus reaches
       * the nucleons and passes through the decay products, and the leptons are where it stops
       * being shared. Reordering for the packer would have thrown that away.
       *
       * The split is a real one either way. A neutrino IS a lepton, and it is also the thing
       * every beta decay equation needs, which is a different reason to go looking for it.
       */
      { labelKey: 'reactions.groups.leptons', items: [
        { sym: 'e⁻', code: 'e^-{}',         aria: 'reactions.symbols.electron'     },
        { sym: 'e⁺', code: 'e^+{}',         aria: 'reactions.symbols.positron'     },
        { sym: 'μ⁻', code: '\\mu^-{}',  aria: 'reactions.symbols.muonMinus' },
        { sym: 'μ⁺', code: '\\mu^+{}',  aria: 'reactions.symbols.muonPlus'  },
        /* The third generation, which had no keys at all: electron and muon were here and the
           tau was not, so two thirds of the lepton table was reachable and one third was not. */
        { sym: 'τ⁻', code: '\\tau^-{}', aria: 'reactions.symbols.tauMinus' },
        { sym: 'τ⁺', code: '\\tau^+{}', aria: 'reactions.symbols.tauPlus'  },
      ] },
      { labelKey: 'reactions.groups.neutrinos', items: [
        { sym: 'ν',  code: '\\nu{}',        aria: 'reactions.symbols.neutrino'     },
        { sym: 'ν̄',  code: '\\bar{\\nu}{}', aria: 'reactions.symbols.antineutrino' },
      ] },
      /* Mesons and muons. The category was called Nuclear and so excluded the particle physics
         that writes exactly the same kind of equation.

         PIONS AND KAONS ARE TWO RUNS, not one of six. They were one group, and the baryons
         below could not be added without it: the panel packs short runs into two columns and
         needs an EVEN number of them, so a ninth group had to arrive with a tenth. The split
         was already written into the old group's own comment — kaons are where strangeness
         enters a syllabus, which is a different lesson from the pion. */
      { labelKey: 'reactions.groups.pions', items: [
        { sym: 'π⁰', code: '\\pi^0{}',  aria: 'reactions.symbols.pionZero'  },
        { sym: 'π⁺', code: '\\pi^+{}',  aria: 'reactions.symbols.pionPlus'  },
        { sym: 'π⁻', code: '\\pi^-{}',  aria: 'reactions.symbols.pionMinus' },
      ] },
      /* Pions alone cannot write a single strange decay, so the group could name the concept
         and not reach it. */
      { labelKey: 'reactions.groups.kaons', items: [
        { sym: 'K⁺', code: 'K^+{}',  aria: 'reactions.symbols.kaonPlus'  },
        { sym: 'K⁻', code: 'K^-{}',  aria: 'reactions.symbols.kaonMinus' },
        { sym: 'K⁰', code: 'K^0{}',  aria: 'reactions.symbols.kaonZero'  },
      ] },
      /*
       * THE HYPERONS, which is the half of the baryon octet that was unreachable.
       *
       * The proton and the neutron are baryons too and are already up in Nuclei, where a
       * chemistry syllabus meets them. These are the strange ones, and without them a decay
       * chain cannot be written at all — the kaons above produce them.
       *
       * SIX, because a run wider than six takes the full panel width and the layout allows
       * only one of those per category, at the front, which is Nuclei's place. The omission
       * is Ξ⁰: every letter the owner asked for is here, and the neutral cascade is the one
       * charge state of the four that a syllabus writes least.
       */
      { labelKey: 'reactions.groups.baryons', items: [
        { sym: 'Λ⁰', code: '\\Lambda^0{}', aria: 'reactions.symbols.lambdaZero' },
        { sym: 'Σ⁺', code: '\\Sigma^+{}',  aria: 'reactions.symbols.sigmaPlus'  },
        { sym: 'Σ⁰', code: '\\Sigma^0{}',  aria: 'reactions.symbols.sigmaZero'  },
        { sym: 'Σ⁻', code: '\\Sigma^-{}',  aria: 'reactions.symbols.sigmaMinus' },
        { sym: 'Ξ⁻', code: '\\Xi^-{}',     aria: 'reactions.symbols.xiMinus'    },
        { sym: 'Ω⁻', code: '\\Omega^-{}',  aria: 'reactions.symbols.omegaMinus' },
      ] },
      { labelKey: 'reactions.groups.bosons', items: [
        /*
         * THE WEAK BOSONS, which belong in an equation and not only in a diagram.
         *
         * `W^- -> e^- + \bar{\nu}` is how beta decay is written once the mechanism is being
         * taught, so these are ordinary notation rather than Feynman furniture.
         *
         * NO GLUON, by the owner's call: it is a diagram object and never appears on a line.
         */
        { sym: 'W⁻', code: 'W^-{}', aria: 'reactions.symbols.wMinus' },
        { sym: 'W⁺', code: 'W^+{}', aria: 'reactions.symbols.wPlus'  },
        { sym: 'Z⁰', code: 'Z^0{}', aria: 'reactions.symbols.zZero'  },
      ] },
      /*
       * THE SIX QUARKS, as KEYS rather than as a search-only group.
       *
       * Forced by the trigger, not chosen. The suggester fires on a run of three lowercase
       * letters, and `up` and `down` are two letters AND ordinary English words: a find-only
       * group would be unreachable by typing for exactly the two a teacher reaches for first,
       * and would misfire on prose for the rest.
       *
       * Each inserts the bare letter, which is what a quark IS in an equation — `d -> u + e^-`
       * — so the key buys the name, the browse and the reading rather than a spelling nobody
       * could guess. An ANTIquark is the bar decoration over one, and the fractional charges
       * live in Charges, since they are written only when the charge is the point.
       *
       * Baryons (Λ Σ Ξ Ω) are deliberately NOT here: the scope left that open for the owner.
       */
      { labelKey: 'reactions.groups.quarks', items: [
        { sym: 'u', code: 'u', aria: 'reactions.symbols.quarkUp'      },
        { sym: 'd', code: 'd', aria: 'reactions.symbols.quarkDown'    },
        { sym: 's', code: 's', aria: 'reactions.symbols.quarkStrange' },
        { sym: 'c', code: 'c', aria: 'reactions.symbols.quarkCharm'   },
        { sym: 'b', code: 'b', aria: 'reactions.symbols.quarkBottom'  },
        { sym: 't', code: 't', aria: 'reactions.symbols.quarkTop'     },
      ] },
    ],
  },
{
    /*
     * DRAWN MOLECULES, on a row of their own rather than inside Organic.
     *
     * The scope said to put the picker in Organic, and it does not fit there: Organic is a flat
     * grid of glyphs and this is a search field over a hundred-odd pictures. Sharing one panel
     * would give that panel two modes. The same decision was already made for Elements, which
     * could equally have been filed under species and is its own keyboard because it is shaped
     * differently. Reversible if it reads wrong in use.
     */
    /*
     * STRUCTURES LEAVES THE CATEGORY ROW, owner's call, and the row is the better for it.
     *
     * Everything else in that row is a GLYPH YOU PICK -- one click from a grid. A structure is
     * an OBJECT YOU AUTHOR: a search, or a group browse, or three menus in the builder, or raw
     * SMILES; it is the only insert you go back and edit afterwards; it carries a name because
     * a drawing cannot be read aloud; and it needs a chemistry library loaded to draw at all.
     * It was the one tab whose panel was not a grid of buttons.
     *
     * The tell was the curly-arrow button, which reached its editor by flipping the CATEGORY
     * ROW -- `setActiveCategory('structures')`. A control in the action row had to reach into
     * the browse row's state, which is what happens when something is shelved in the wrong
     * place. It now opens a panel that no chip owns, which is the same call made honestly.
     *
     * `findOnly` rather than deleted, exactly as Elements and Organic: the 122 molecules stay
     * in the name index and the suggester goes on offering them as you type.
     */
    id: 'structures', labelKey: 'reactions.categories.structures', findOnly: true,
    structures: true, items: [],
  },
{
    id: 'elements', labelKey: 'reactions.categories.elements', findOnly: true, elements: true,
    items: SCHOOL_ELEMENTS.map(asElement),
  },
{
    id: 'organic', labelKey: 'reactions.categories.organic', findOnly: true,
    items: [
      /* Functional groups. All of these rendered before they had buttons, which meant a teacher
         who did not already know mhchem could not reach them. */
      { sym: '–OH',   code: '-OH',   aria: 'reactions.symbols.hydroxyl'  },
      { sym: '–COOH', code: '-COOH', aria: 'reactions.symbols.carboxyl'  },
      { sym: '–CHO',  code: '-CHO',  aria: 'reactions.symbols.aldehyde'  },
      { sym: '–CO–',  code: '-CO-',  aria: 'reactions.symbols.carbonyl'  },
      { sym: '–COO–', code: '-COO-', aria: 'reactions.symbols.ester'     },
      { sym: '–NH₂',  code: '-NH2',  aria: 'reactions.symbols.amine'     },
      { sym: 'C₆H₅–', code: 'C6H5-', aria: 'reactions.symbols.phenyl'    },
      { sym: 'R',     code: 'R',     aria: 'reactions.symbols.rGroup'    },
      { sym: "R'",    code: "R'",    aria: 'reactions.symbols.rGroupPrime' },
      /* Lone pairs used to sit here as two frames with a hole. They are a MARK now, in the
         styling row with crossing out and the oxidation number, because that is what they are:
         something added to an atom you already wrote, not something you insert and then fill.
         As inserts they also leaked `$\\overset{..}{}$` into the field as literal text, which
         was the one place in this editor a teacher met raw LaTeX. */
      /* A repeat unit and its subscript n. Both forms, because a polymer is written either way. */
      { sym: '(x)ₙ', code: '()_n',  cursor: 1, aria: 'reactions.symbols.repeatUnit'   },
      { sym: '–[x]–', code: '-[]-', cursor: 2, aria: 'reactions.symbols.repeatBracket' },
    ],
  },
{
    /*
     * CHARGES, BACK — AND REACHABLE ONLY BY TYPING THEIR NAME.
     *
     * The owner removed them from the palette with "remove charges, i have another idea for
     * them", and this is the other idea. They are nine keys that would crowd a browse row and
     * nine things a teacher knows the name of, which is exactly what `findOnly` is for: kept in
     * the name index, offered as you type, never given a tab. Elements and Organic are already
     * here on the same terms.
     *
     * NO TRIGGER CHARACTER, and not for want of looking. `^` is mhchem's own charge operator
     * and would fight a hand-typed `Ca^2+`; `[`, `(`, `<`, `_`, `-`, `=`, `#` and `*` are all
     * taken by the notation. See [[feedback_no_prefix_sigil_triggers]] and `reaction-suggest`:
     * a lowercase run is never valid chemistry, which is the discriminator this editor has.
     *
     * The magnitudes are listed rather than constructed. A query has to END at the caret, so a
     * digit switches the suggester off and `Caplus2` can never work — and making the digit a
     * magnitude would turn a lookup into a constructor. Three entries get there in one tap.
     */
    id: 'charges', labelKey: 'reactions.categories.charges', findOnly: true,
    items: [
      { sym: '\u207A',  code: '^+',  aria: 'reactions.symbols.chargePlus',   alias: 'reactions.aliases.cation' },
      { sym: '\u00B2\u207A', code: '^2+', aria: 'reactions.symbols.chargePlus2',  alias: 'reactions.aliases.cation' },
      { sym: '\u00B3\u207A', code: '^3+', aria: 'reactions.symbols.chargePlus3',  alias: 'reactions.aliases.cation' },
      /* FOUR, because 3+ stops short of ions a syllabus actually writes: Ce⁴⁺, Sn⁴⁺, Ti⁴⁺,
         Pb⁴⁺. Five and up exist as OXIDATION STATES rather than as free ions, and those are
         the other control — it already offers +5 and is the right home for them. */
      { sym: '\u2074\u207A', code: '^4+', aria: 'reactions.symbols.chargePlus4',  alias: 'reactions.aliases.cation' },
      /* `borrowed` keeps this one OUT OF THE DISPLAY TABLE, which is what the flag does and
         why it is here. `e^-{}` already owns the `^-` mapping, so admitting a bare one would
         turn every `2e^-` a teacher writes into an electron. The key still inserts `^-`. */
      { sym: '\u207B',  code: '^-',  aria: 'reactions.symbols.chargeMinus',  alias: 'reactions.aliases.anion', borrowed: true },
      { sym: '\u00B2\u207B', code: '^2-', aria: 'reactions.symbols.chargeMinus2', alias: 'reactions.aliases.anion' },
      { sym: '\u00B3\u207B', code: '^3-', aria: 'reactions.symbols.chargeMinus3', alias: 'reactions.aliases.anion' },
      /* Carbide and silicide are 4−, and they are the floor: nothing a school syllabus writes
         carries more negative charge than that. */
      { sym: '\u2074\u207B', code: '^4-', aria: 'reactions.symbols.chargeMinus4', alias: 'reactions.aliases.anion' },
      /*
       * FRACTIONAL CHARGES, which only a quark carries.
       *
       * Here rather than beside the quark keys because they are written only when the charge
       * is the point — `d -> u + e^-` carries none — and because Charges is already the home
       * for "a superscript marker on a species". All four magnitudes, since an ANTIquark takes
       * the opposite sign of its quark.
       */
      { sym: '\u207A\u2154', code: '^{+2/3}', aria: 'reactions.symbols.chargeTwoThirdsPlus',  alias: 'reactions.aliases.fraction' },
      { sym: '\u207B\u2153', code: '^{-1/3}', aria: 'reactions.symbols.chargeThirdMinus',     alias: 'reactions.aliases.fraction' },
      { sym: '\u207B\u2154', code: '^{-2/3}', aria: 'reactions.symbols.chargeTwoThirdsMinus', alias: 'reactions.aliases.fraction' },
      { sym: '\u207A\u2153', code: '^{+1/3}', aria: 'reactions.symbols.chargeThirdPlus',      alias: 'reactions.aliases.fraction' },
      /* mhchem's own radical dot. Raised, where the hydrate dot sits on the line. */
      { sym: '\u2022',  code: '^.',  aria: 'reactions.symbols.radicalDot',   alias: 'reactions.aliases.radical' },
      /* Bond polarity, which is a charge on a species that has not been ionised. */
      { sym: '\u03B4\u207A', code: '^{\\delta+}', aria: 'reactions.symbols.partialPlus',  alias: 'reactions.aliases.partial' },
      { sym: '\u03B4\u207B', code: '^{\\delta-}', aria: 'reactions.symbols.partialMinus', alias: 'reactions.aliases.partial' },
    ],
  },
];

export const CATEGORIES: Category[] = RAW_CATEGORIES.map(c => ({
  ...c,
  items: c.items ?? (c.groups ?? []).flatMap(g => g.items),
}));

/** The categories that get a tab. The rest are reachable by name, through the search. */
export const browsableCategories = (): Category[] => CATEGORIES.filter(c => !c.findOnly);

/**
 * WHAT A CODE LOOKS LIKE, TAKEN FROM THE BUTTON THAT INSERTS IT.
 *
 * The palette already names every symbol it offers, so the editor reads the glyph off the same
 * row rather than deriving it a second way. Built from `CATEGORIES`, so a button added above is
 * drawn correctly in the field without a second edit — the drift this closes had the gas arrow
 * inserting a literal `^` and the negative charge a literal `^-`.
 */
export const GLYPH_FOR_CODE: ReadonlyMap<string, string> = new Map(
  CATEGORIES.filter(c => !c.elements).flatMap(c => c.items.map(i => [i.code, i.sym] as const)),
);

/**
 * The codes the field DRAWS rather than types, which is also the set that has to be atomic.
 *
 * An SVG cannot survive as text: the moment a drawn symbol round-trips through source as a
 * plain code it comes back as whatever `toDisplay` makes of it, and the arrow the teacher
 * pressed turns into two characters. Deriving the atom list from this one is what keeps the
 * two facts from being stated separately and drifting — which is exactly what happened when
 * ⇄ and ⇌ became drawn and the hand-written atom list still named only three codes.
 */
export const DRAWN_CODES: readonly string[] = [...CATEGORIES.filter(c => !c.elements)
  .flatMap(c => c.items)
  .filter(i => i.special && i.special !== 'nuclide')
  .map(i => i.code)];

/**
 * THE ARROWS, as the codes that write them.
 *
 * Needed wherever something has to recognise "an arrow" rather than one particular arrow: the
 * parser that reads a labelled arrow back out of mhchem has to admit every arrow that can carry
 * a label, which is all of them, and a decoration applies to whichever one is selected.
 *
 * Derived from the drawings rather than listed, so an arrow added to the palette is an arrow
 * everywhere without a second edit — the trap that left the atom list naming three codes after
 * ⇄ and ⇌ became drawn.
 */
export const ARROW_CODES: readonly string[] = CATEGORIES.filter(c => !c.elements)
  .flatMap(c => c.items)
  .filter(i => i.special && i.special !== 'nuclide' && i.special in ARROWS)
  .map(i => i.code);

/** The codes whose glyph has to be DRAWN, and which drawing each one wants. */
export const SPECIAL_FOR_CODE: ReadonlyMap<string, NonNullable<Sym['special']>> = new Map(
  CATEGORIES.filter(c => !c.elements)
    .flatMap(c => c.items.filter(i => i.special).map(i => [i.code, i.special!] as const)),
);

/**
 * Where an element sits in the periodic table, by atomic number.
 *
 * Rows 1–7 are the periods. The f-block gets rows 9 and 10, with row 8 left empty — the gap the
 * table is always drawn with, and the reason lanthanum and actinium appear below rather than
 * stretching the table to 32 columns.
 *
 * Derived rather than tabulated: 118 hand-written pairs is 118 chances to put niobium in the
 * wrong group, and the blocks are regular enough that the arithmetic is the shorter statement.
 */
export function periodicPosition(z: number): { group: number; row: number } {
  if (z === 1) return { group: 1, row: 1 };
  if (z === 2) return { group: 18, row: 1 };
  if (z <= 10) return { group: z <= 4 ? z - 2 : z + 8, row: 2 };
  if (z <= 18) return { group: z <= 12 ? z - 10 : z, row: 3 };
  if (z <= 36) return { group: z - 18, row: 4 };
  if (z <= 54) return { group: z - 36, row: 5 };
  if (z <= 56) return { group: z - 54, row: 6 };
  if (z <= 71) return { group: z - 54, row: 9 };   // lanthanides
  if (z <= 86) return { group: z - 68, row: 6 };
  if (z <= 88) return { group: z - 86, row: 7 };
  if (z <= 103) return { group: z - 86, row: 10 }; // actinides
  return { group: z - 100, row: 7 };
}

/** The table as a grid of rows, with a null wherever the table has a hole. */
export function periodicGrid(): (string | null)[][] {
  const rows: (string | null)[][] = Array.from({ length: 10 }, () => Array(18).fill(null));
  ALL_ELEMENTS.forEach((sym, i) => {
    const { group, row } = periodicPosition(i + 1);
    rows[row - 1]![group - 1] = sym;
  });
  return rows.filter(r => r.some(Boolean));
}

/** The short set, in rows of ten, so arrowing down moves down by what the eye sees. */
export function schoolGrid(): (string | null)[][] {
  const rows: (string | null)[][] = [];
  for (let i = 0; i < SCHOOL_ELEMENTS.length; i += 10) {
    const row: (string | null)[] = [...SCHOOL_ELEMENTS.slice(i, i + 10)];
    while (row.length < 10) row.push(null);
    rows.push(row);
  }
  return rows;
}

/**
 * ── COMMON, DERIVED FROM THE REACTIONS THE DOCUMENT ALREADY HOLDS ─────────────────────────────
 *
 * This band was a hardcoded seven, defended on the grounds that a reaction is written with
 * arrows and states whatever the branch of chemistry — unlike maths, where a logician's symbols
 * and a physicist's do not overlap at all. True as far as it goes, and still too coarse: a
 * nuclear physics task is written with nuclides and decay products and not one of `(s) (l) (g)
 * (aq)`, and an organic one reaches for bonds and labelled arrows. The owner had assumed this
 * was already derived — it was true of the equation editor and false here. This closes that.
 *
 * ── WHAT IT RANKS, AND WHAT IT LEAVES OUT ─────────────────────────────────────────────────────
 * Only the categories with a TAB. Elements, Organic and Structures are `findOnly`: reached by
 * typing their name, which is already the fast path for them — and elements would swamp every
 * count anyway, since every formula in every document is made of C, H and O.
 *
 * It does NOT exclude what a keyboard can type, which is the one rule the equation editor's
 * band lives by. The reason that rule exists is that a button for `=` is worth nothing to
 * someone who can press `=`, and it does not carry over: in this field `-` draws a proper bond,
 * `(s)` italicises and `->` becomes a drawn arrow, none of which a teacher can see from the
 * keyboard. The whole point of the reaction palette is that the mhchem spelling is not
 * guessable, so the ASCII-looking keys are exactly the ones worth offering.
 *
 * ── WHY IT TOKENISES RATHER THAN COUNTING OCCURRENCES ─────────────────────────────────────────
 * Counting each code's occurrences separately makes the single bond the most-used symbol in
 * every document ever written, because `-` is inside `->`, `<-`, `-OH` and `->[\Delta]`. The
 * source is walked instead, longest match first, each match consumed: `->[\Delta]` is one heat
 * arrow and not an arrow plus a bond plus a delta. Ties go to whichever the palette lists first.
 */

/** The hand-picked seven this band used to be, now the top-up for a document with little to say. */
const COMMON_FALLBACK: readonly string[] = ['->', '<=>', '(s)', '(l)', '(g)', '(aq)', '\\Delta'];

/** The most the band will ever hold. What shows is what fits on one line — `useOneLineSlots`. */
export const COMMON_SLOTS = 12;

/**
 * A code as a pattern that matches what the field actually stores.
 *
 * A hole is kept as a hole: `->[]` has to match `->[cat]`, and `^{}_{}` has to match `^{235}_{92}`,
 * or the nuclide template — the one key a teacher writing any nuclide starts from — would never
 * count once. Everything else is literal.
 */
function patternFor(code: string): RegExp {
  const escaped = code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const withHoles = escaped
    .replace(/\\\{\\\}/g, '\\{[^{}]*\\}')
    .replace(/\\\[\\\]/g, '\\[[^\\[\\]]*\\]');
  return new RegExp(`^${withHoles}${boundaryAfter(code)}`);
}

/**
 * What may NOT follow a match, where the code is a prefix of something else entirely.
 *
 * Two cases, both real. A bare `^` is the gas-evolved arrow AND the start of every charge ever
 * written, so `Ca^2+` would otherwise be counted as a species bubbling off. And a code ending
 * in a letter is a prefix of a longer word: `\Delta` sits inside nothing today, but the same
 * trap cost the equation editor a bug where `\in` was found inside every `\infty`.
 */
function boundaryAfter(code: string): string {
  if (code.endsWith('^')) return '(?![\\w{+\\-\\\\])';
  return /[a-zA-Z]$/.test(code) ? '(?![a-zA-Z])' : '';
}

/** Every symbol Common may offer, in the order the palette lists them. */
function rankableSymbols(): Sym[] {
  return browsableCategories().flatMap(c => c.items);
}

/**
 * How many times each symbol is written across `sources`, by longest match.
 *
 * Keyed by code. A code that matches nothing is absent rather than zero, so the caller can tell
 * "the document uses this" from "the document does not".
 */
export function countSymbols(sources: readonly string[]): Map<string, number> {
  const candidates = rankableSymbols().map(sym => ({ code: sym.code, re: patternFor(sym.code) }));
  const counts = new Map<string, number>();

  for (const source of sources) {
    let i = 0;
    while (i < source.length) {
      let best: { code: string; length: number } | null = null;
      const rest = source.slice(i);
      for (const c of candidates) {
        const m = c.re.exec(rest);
        /* Strictly longer, so a tie leaves the earlier-listed candidate in place. */
        if (m && m[0].length > (best?.length ?? 0)) best = { code: c.code, length: m[0].length };
      }
      if (best) {
        counts.set(best.code, (counts.get(best.code) ?? 0) + 1);
        i += best.length;
      } else {
        i += 1;
      }
    }
  }
  return counts;
}

/**
 * The Common band for a document: what it already writes, topped up from the fallback.
 *
 * `sources` is every reaction in the document, the one being edited first — ties break towards
 * whatever comes earlier, so the reaction in front of the teacher wins them. `slots` is how many
 * keys the band can show, measured from the rendered row rather than assumed.
 *
 * Ranked for membership, laid out in taxonomy order: a key never trades places with its
 * neighbour because the document used it twice more.
 */
export function commonFor(sources: readonly string[], slots: number = COMMON_SLOTS): Sym[] {
  if (slots <= 0) return [];
  const symbols = rankableSymbols();
  const counts = countSymbols(sources);

  const chosen = new Map<string, { sym: Sym; order: number }>();
  symbols
    .map((sym, order) => ({ sym, order, count: counts.get(sym.code) ?? 0 }))
    .filter(e => e.count > 0)
    .sort((a, b) => b.count - a.count || a.order - b.order)
    .slice(0, slots)
    .forEach(e => chosen.set(e.sym.code, e));

  for (const code of COMMON_FALLBACK) {
    if (chosen.size >= slots) break;
    if (chosen.has(code)) continue;
    const order = symbols.findIndex(s => s.code === code);
    if (order < 0) throw new Error(`Common names a code the palette does not have: ${code}`);
    chosen.set(code, { sym: symbols[order]!, order });
  }

  return [...chosen.values()].sort((a, b) => a.order - b.order).map(e => e.sym);
}
